import React, { PureComponent } from 'react';
import './ResultsChart.css';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  YAxis,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';

/**
 * Minimal chart:
 * - Hide Y-axis labels
 * - Show no horizontal grid lines
 * - Show both "portfolio_value" and "cash" in tooltip (cash is tooltip-only, not plotted)
 */

export const getPortfolioValueDomain = (rows) => {
  const values = (Array.isArray(rows) ? rows : [])
    .flatMap((row) => [row?.portfolio_value, row?.cash_adjusted_portfolio_value])
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return [0, 100];
  }

  let minVal = values.reduce((min, value) => Math.min(min, value), Infinity);
  let maxVal = values.reduce((max, value) => Math.max(max, value), -Infinity);

  const range = maxVal - minVal;
  const padding = range > 0
    ? range * 0.08
    : Math.max(Math.abs(maxVal) * 0.02, 1);

  minVal -= padding;
  maxVal += padding;

  return [minVal, maxVal];
};

const markerTimestamp = (marker) => marker?.timestamp || marker?.occurredAt || marker?.occurred_at || null;

export const filterMarkersForChart = (markers, rows, frequency = 'day') => {
  const dates = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.date))
    .filter(Number.isFinite);
  if (dates.length === 0) return [];
  const first = Math.min(...dates);
  const last = Math.max(...dates);
  const bucketMs = frequency === 'minute'
    ? 60 * 1000
    : frequency === 'hour'
      ? 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;
  return (Array.isArray(markers) ? markers : [])
    .flatMap((marker) => {
      const timestamp = new Date(markerTimestamp(marker)).getTime();
      return Number.isFinite(timestamp) && timestamp >= first && timestamp < last + bucketMs
        ? [{ ...marker, chartTimestamp: timestamp }]
        : [];
    })
    .sort((left, right) => (
      left.chartTimestamp - right.chartTimestamp
      || String(left.id || '').localeCompare(String(right.id || ''))
    ));
};

export const insertChartGapBreaks = (rows, gaps) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const breakRows = (Array.isArray(gaps) ? gaps : []).flatMap((gap, index) => {
    const start = new Date(gap?.start).getTime();
    const end = new Date(gap?.end).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
    return [{
      date: start + ((end - start) / 2),
      portfolio_value: null,
      cash_adjusted_portfolio_value: null,
      spy_benchmark: null,
      chart_gap_id: gap.id || `gap-${index}`,
    }];
  });
  return [...safeRows, ...breakRows].sort((left, right) => Number(left.date) - Number(right.date));
};

const markerType = (marker) => marker?.marker_type || marker?.markerType || 'event';

const markerTitle = (marker) => {
  const type = markerType(marker);
  if (type === 'deposit') return 'Deposit';
  if (type === 'withdrawal') return 'Withdrawal';
  if (type === 'deployment_start') return 'Deployment started';
  if (type === 'deployment_stop') return 'Deployment stopped';
  return marker?.label || 'Account event';
};

const markerDetails = (marker) => {
  const type = markerType(marker);
  if (type === 'deposit' || type === 'withdrawal') {
    const amount = Number(marker?.amount);
    const formatted = Number.isFinite(amount)
      ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: marker?.currency || 'USD',
      }).format(Math.abs(amount))
      : '';
    return [formatted, marker?.description].filter(Boolean).join(' · ');
  }
  const revision = marker?.revision_version ?? marker?.revisionVersion;
  const mode = String(marker?.deployment_mode || marker?.deploymentMode || '').replace(/_/g, ' ');
  return [
    revision !== null && revision !== undefined ? `Revision ${revision}` : null,
    mode ? mode.replace(/\b\w/g, (character) => character.toUpperCase()) : null,
    marker?.cadence || null,
  ].filter(Boolean).join(' · ');
};

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload, frequency } = this.props;
    const date = new Date(payload.value);

    let formattedDate;
    switch (frequency) {
      case 'minute':
        formattedDate = moment(date).format('MMM D, h:mm a');
        break;
      case 'hour':
        formattedDate = moment(date).format('MMM D, h A');
        break;
      case 'day':
      default:
        formattedDate = moment(date).format('MMM D');
        break;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#cccccc"
          fontSize={10}
          transform="rotate(-10)"
        >
          {formattedDate}
        </text>
      </g>
    );
  }
}

export default function ResultsChart({
  data,
  frequency,
  isLoading,
  trendDirection,
  minimal = false,
  chartHeight,
  emptyTitle = 'No data for this timeframe.',
  emptyDescription = '',
  markers = [],
  gaps = [],
  cashFlowAvailability,
  isStale = false,
}) {
  const [clickedMarkerId, setClickedMarkerId] = React.useState(null);
  const [focusedMarkerId, setFocusedMarkerId] = React.useState(null);
  const [hoveredMarkerId, setHoveredMarkerId] = React.useState(null);
  if (isLoading) {
    return (
      <div className="chart-loading fade-in">
        <Spinner
          animation="border"
          role="status"
          aria-label="Loading chart data"
          className="chart-loading-spinner"
        />
        <h3 style={{ color: '#cccccc', marginTop: '1rem' }}>Loading Chart Data...</h3>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="no-chart-data fade-in">
        <h3>{emptyTitle}</h3>
        {emptyDescription && <p>{emptyDescription}</p>}
      </div>
    );
  }

  // Decide portfolio line color
  let lineColor = '#4caf50'; // default green
  if (trendDirection === 'down') {
    lineColor = '#f44336';
  }

  const chartData = insertChartGapBreaks(data, gaps);
  const visibleMarkers = filterMarkersForChart(markers, data, frequency);
  const activeMarkerId = hoveredMarkerId || focusedMarkerId || clickedMarkerId;
  const activeMarker = visibleMarkers.find((marker) => marker.id === activeMarkerId) || null;
  const yDomain = getPortfolioValueDomain(chartData);
  const hasAdjustedValues = chartData.some((row) => (
    typeof row?.cash_adjusted_portfolio_value === 'number'
    && row.cash_adjusted_portfolio_value !== row.portfolio_value
  ));

  const tooltipFormatter = (date) => moment(date).local().format('MMM D, YYYY h:mm a');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const {
        portfolio_value,
        cash_adjusted_portfolio_value,
        cash,
        spy_benchmark,
      } = payload[0].payload || {};

      return (
        <Card className="tooltip-card fade-in">
          <Card.Body>
            {typeof portfolio_value === 'number' && (
              <Card.Title className="tooltip-title">
                <span style={{ color: lineColor }} className="chart-tooltip-name">
                  Account Value:
                </span>
                &nbsp;
                <NumericFormat
                  value={portfolio_value}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof cash === 'number' && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#ffa500' }} className="chart-tooltip-name">
                  Cash:
                </span>
                &nbsp;
                <NumericFormat
                  value={cash}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof cash_adjusted_portfolio_value === 'number' && hasAdjustedValues && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#ffb74d' }} className="chart-tooltip-name">
                  Cash-adjusted Value:
                </span>
                &nbsp;
                <NumericFormat
                  value={cash_adjusted_portfolio_value}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof spy_benchmark === 'number' && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#999999' }} className="chart-tooltip-name">
                  Benchmark:
                </span>
                &nbsp;
                <NumericFormat
                  value={spy_benchmark}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            <Card.Text className="tooltip-text">{tooltipFormatter(label)}</Card.Text>
          </Card.Body>
        </Card>
      );
    }
    return null;
  };

  return (
    <div className="chart-container fade-in">
      {isStale && <div className="chart-data-note" role="status">Latest account data is stale.</div>}
      {cashFlowAvailability?.status && cashFlowAvailability.status !== 'available' && (
        <div className="chart-data-note" role="status">
          Cash-flow adjustment {cashFlowAvailability.status}. {cashFlowAvailability.reason || ''}
        </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight || 400}>
        <LineChart
          data={chartData}
          margin={minimal ? { top: 12, right: 8, left: 8, bottom: 8 } : { top: 10, right: 10, left: 0, bottom: 20 }}
        >
          {!minimal && <CartesianGrid stroke="#333" vertical={false} horizontal={false} />}
          {!minimal && (
            <XAxis
              dataKey="date"
              tick={<CustomizedAxisTick frequency={frequency} />}
              type="number"
              scale="time"
              domain={['auto', 'auto']}
              stroke="#cccccc"
            />
          )}
          {minimal && <XAxis dataKey="date" type="number" scale="time" domain={['auto', 'auto']} hide />}
          <YAxis domain={yDomain} hide />
          <Tooltip content={<CustomTooltip />} />
          {!minimal && (
            <Legend
              verticalAlign="top"
              height={30}
              wrapperStyle={{ color: '#ffffff', fontSize: '0.85rem' }}
            />
          )}

          {visibleMarkers.map((marker) => {
            const type = markerType(marker);
            const isCashFlow = type === 'deposit' || type === 'withdrawal';
            return (
              <ReferenceLine
                key={marker.id}
                x={marker.chartTimestamp}
                stroke={isCashFlow ? '#ffb74d' : '#64b5f6'}
                strokeDasharray={type === 'deployment_stop' ? '3 3' : '5 3'}
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{
                  value: type === 'deposit' ? '+' : type === 'withdrawal' ? '-' : type === 'deployment_start' ? 'Start' : 'Stop',
                  fill: isCashFlow ? '#ffb74d' : '#90caf9',
                  fontSize: 10,
                  position: 'insideTopRight',
                }}
              />
            );
          })}

          <Line
            type="monotone"
            dataKey="portfolio_value"
            name="Account Value"
            stroke={lineColor}
            strokeWidth={4}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          {!minimal && hasAdjustedValues && (
            <Line
              type="monotone"
              dataKey="cash_adjusted_portfolio_value"
              name="Cash-adjusted Value"
              stroke="#ffb74d"
              strokeWidth={3}
              strokeDasharray="7 4"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          )}
          {!minimal && (
            <Line
              type="monotone"
              dataKey="spy_benchmark"
              name="SPY (Benchmark)"
              stroke="#999999"
              strokeWidth={4}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      {visibleMarkers.length > 0 && (
        <div className="chart-marker-strip" aria-label="Chart events">
          {visibleMarkers.map((marker) => (
            <button
              aria-expanded={activeMarker?.id === marker.id}
              className={`chart-marker-button chart-marker-button--${markerType(marker)}`}
              key={marker.id}
              onBlur={() => setFocusedMarkerId((current) => current === marker.id ? null : current)}
              onClick={() => setClickedMarkerId(marker.id)}
              onFocus={() => setFocusedMarkerId(marker.id)}
              onMouseEnter={() => setHoveredMarkerId(marker.id)}
              onMouseLeave={() => setHoveredMarkerId((current) => current === marker.id ? null : current)}
              type="button"
            >
              <span>{markerTitle(marker)}</span>
              <time dateTime={new Date(marker.chartTimestamp).toISOString()}>
                {moment(marker.chartTimestamp).format('MMM D')}
              </time>
            </button>
          ))}
          {activeMarker && (
            <div className="chart-marker-detail" role="status">
              <strong>{markerTitle(activeMarker)}</strong>
              <span>{markerDetails(activeMarker)}</span>
              <time dateTime={new Date(activeMarker.chartTimestamp).toISOString()}>
                {tooltipFormatter(activeMarker.chartTimestamp)}
              </time>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
